import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateIntegrationQuery,
	useGetExternalIntegrationQuery,
	usePostExternalIntegrationConfigurationMutation,
} from '../../../../queries';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import serviceTitanLogo from '../../../assets/logo_servicetitan_banner.png';
import { Button } from '../../../components/Button';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DangerIcon } from '../../../components/svgs/icons/DangerIcon';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { Input } from '../../../components-v2/Input';
import { nameCircles } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Configure = 'configure',
	Manage = 'manage',
}

enum IntegrationState {
	NotConfigured = 'not-configured',
	NotConnected = 'not-connected',
}

export function ServiceTitan() {
	const [tenantId, setTenantId] = React.useState('');
	const history = useHistory();
	const userSession = useUserSession();
	const adminUserSession = userSession as AdminUserSessionContext;
	const params = useParams<{ tab: TabValues }>();
	const [isEditing, setIsEditing] = React.useState(false);

	const postExternalIntegrationConfigurationMutation = usePostExternalIntegrationConfigurationMutation({
		onError: error => {
			toast.error(error.systemMessage);
		},
		onSuccess: () => {
			invalidateIntegrationQuery({ provider: Api.IntegrationProvider.ServiceTitan });
			toast.success('Successfully saved the configuration settings.');
		},
	});

	const integrationQuery = useGetExternalIntegrationQuery({
		provider: Api.IntegrationProvider.ServiceTitan,
	});

	const changeTab = (value: string) => {
		history.replace({
			pathname: `/integrations/${Api.IntegrationProvider.ServiceTitan}/${value}`,
		});
	};

	const integrationState = React.useMemo(() => {
		const configuration = integrationQuery.data?.configuration as Api.IServiceTitanIntegrationConfiguration;

		return !configuration?.tenantId ? IntegrationState.NotConfigured : IntegrationState.NotConnected;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationQuery.data]);

	React.useEffect(() => {
		if (integrationQuery.data?.configuration) {
			const configuration = integrationQuery.data?.configuration as Api.IServiceTitanIntegrationConfiguration;
			setTenantId(configuration.tenantId);
		}
	}, [integrationQuery.data?.configuration]);

	React.useEffect(() => {
		if (!integrationQuery.data) {
			return;
		}

		if (integrationState === IntegrationState.NotConfigured) {
			if (adminUserSession.isAdmin) {
				changeTab(TabValues.Configure);
			} else {
				changeTab(TabValues.Manage);
			}
		} else {
			changeTab(TabValues.Manage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integrationState, adminUserSession.isAdmin, integrationQuery.data]);

	const saveConfigurationSettings = async (updatedSettings: Partial<Api.IServiceTitanIntegrationConfiguration>) => {
		postExternalIntegrationConfigurationMutation.mutateAsync({
			integrationProvider: Api.IntegrationProvider.ServiceTitan,
			configuration: updatedSettings,
		});
	};

	const onSave = async () => {
		saveConfigurationSettings({ tenantId });
	};

	const onEdit = () => {
		setIsEditing(true);
	};

	const onCancelEdit = () => {
		setIsEditing(false);

		const configuration = integrationQuery.data?.configuration as Api.IServiceTitanIntegrationConfiguration;
		setTenantId(configuration?.tenantId);
	};

	if (integrationQuery.isPending || postExternalIntegrationConfigurationMutation.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	const hasSaveConfigurationError = postExternalIntegrationConfigurationMutation.error;

	const isNotConfigured = integrationState === IntegrationState.NotConfigured;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(styleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={serviceTitanLogo} alt='ServiceTitan Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with ServiceTitan, and pull client information, including names, email, phone number, and
				more.
			</p>

			<Tabs.Root className={`tab-view ${css(styleSheet.tabView)}`} value={params.tab} onValueChange={changeTab}>
				<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
					{adminUserSession.isAdmin ? (
						<Tabs.Trigger
							value={TabValues.Configure}
							className={`tab-view-tabbar-item ${
								params.tab === TabValues.Configure ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<SuccessCheckIcon
								fillColor={isNotConfigured ? nameCircles : undefined}
								ringColor={isNotConfigured ? '#F9F9F9' : undefined}
							/>
							<span>Configure</span>
						</Tabs.Trigger>
					) : null}

					<Tabs.Trigger
						value={TabValues.Manage}
						className={`tab-view-tabbar-item ${
							params.tab === TabValues.Manage ? `tabbar-item-selected ` + css(styleSheet.tabItemSelected) : ''
						} ${css(styleSheet.tabViewItem)}`}
					>
						<span>Manage</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value={TabValues.Configure} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						{isNotConfigured ? (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								To get started, please enter the tenant ID to your ServiceTitan account.
							</p>
						) : (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								Your ServiceTitan account is configured to sync with Levitate.
							</p>
						)}

						{isNotConfigured || isEditing ? (
							<div>
								<div className={css(styleSheet.signInContainer)}>
									<div className={css(styleSheet.signInForm)}>
										<div className={css(styleSheet.inputWrapper)}>
											<span className={css(styleSheet.signInFormFieldLabel)}>Tenant ID</span>
											<div className={css(styleSheet.signInFormField)}>
												<Input
													autoComplete='off'
													autoFocus={true}
													id='service-titan-tenant-id'
													onChange={ev => setTenantId(ev.target.value)}
													type='text'
													value={tenantId}
													styles={[styleSheet.textInput]}
												/>
												{hasSaveConfigurationError && <DangerIcon backgroundColor='#fff' />}
											</div>
										</div>
									</div>
									<div className={css(bs.horizontalStack)}>
										<Button onClick={onSave} kind='primary'>
											Save
										</Button>

										{isEditing ? (
											<Button onClick={onCancelEdit} kind='primary'>
												Cancel
											</Button>
										) : null}
									</div>
								</div>
							</div>
						) : (
							<div>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									<b>Tenant ID:</b> {tenantId}
								</p>

								<Button onClick={onEdit} kind='primary'>
									Edit
								</Button>
							</div>
						)}
					</div>
				</Tabs.Content>

				<Tabs.Content value={TabValues.Manage} className='tab-view-content'>
					<div className={css(styleSheet.section)}>
						<p className={css(integrationsStyleSheet.bodyText)}>
							Once you are configured, your ServiceTitan data will show up in the contacts tab.
						</p>
						<Link to='/people' className={css(bs.ctaButton)}>
							Contacts
						</Link>
					</div>
				</Tabs.Content>
			</Tabs.Root>
		</div>
	);
}
